import {
  fetchRecipes,
  fetchRecipesfilter,
  fetchCuisines,
  fetchTags,
  PostRecipeFavoriteApi,
  fetchFavoriteRecipes,
  fetchRecipeAutoComplete,
} from "../../api/recipesListApi";
import * as Endpoints from "../../entities/Endpoints";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

export const fetchRecipesThunk = createAsyncThunk(
  Endpoints.GET_RECIPES_LIST,
  async (coachId, { rejectWithValue }) => {
    try {
      const data = await fetchRecipes(coachId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchFavoriteRecipesThunk = createAsyncThunk(
  Endpoints.FETCH_FAVORITE_RECIPE,
  async ({ coachId }, { rejectWithValue }) => {
    try {
      const data = await fetchFavoriteRecipes(coachId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRecipesByNameThunk = createAsyncThunk(
  Endpoints.GET_STANDARD_RECIPES,
  async ({ requestPayload, mealTime }, { rejectWithValue }) => {
    try {
      const data = await fetchRecipesfilter({ requestPayload, mealTime });
      return { data, requestedPage: requestPayload.pagination.pageNumber };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCuisinesThunk = createAsyncThunk(
  Endpoints.GET_CUISINES,
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchCuisines();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const postFavoriteRecipeThunk = createAsyncThunk(
  Endpoints.POST_MARK_AS_COMPLETE,
  async ({ coachId, recipeId }, { rejectWithValue }) => {
    try {
      const data = await PostRecipeFavoriteApi({ coachId, recipeId });
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTagsThunk = createAsyncThunk(
  Endpoints.GET_TAGS,
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchTags();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAutocompleteRecipesThunk = createAsyncThunk(
  Endpoints.FETCH_AUTO_COMPLETE,
  async (searchText, { rejectWithValue }) => {
    try {
      const data = await fetchRecipeAutoComplete(searchText);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const recipesListSlice = createSlice({
  name: "getRecipes",
  initialState: {
    recipes: [],
    apiCuisines: null,
    autoCompleteNames: null,
    apiTags: null,
    isloading: false,
    error: null,
    successMessage: null,
    updateSuccessMessage: "",
    updateFailedMessage: "",
    // frontend derived
    pageNumber: 1,
    pageSize: 20,
    hasMore: true,
  },
  reducers: {
    clearUpdateMessage: (state) => {
      state.updateSuccessMessage = "";
      state.updateFailedMessage = "";
    },
    resetRecipes: (state) => {
      state.recipes = [];
      state.pageNumber = 1;
      state.hasMore = true;
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipesThunk.pending, (state) => {
        state.isloading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      .addCase(fetchRecipesThunk.fulfilled, (state, action) => {
        state.isloading = false;
        state.recipes = action.payload;
      })
      .addCase(fetchRecipesThunk.rejected, (state, action) => {
        state.isloading = false;
        state.error = action.payload;
      })

      .addCase(fetchRecipesByNameThunk.pending, (state) => {
        state.isloading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      .addCase(fetchRecipesByNameThunk.fulfilled, (state, action) => {
        state.isloading = false;
        const { data: newData, requestedPage } = action.payload;
        const recipes = newData || [];

        // Update page number
        state.pageNumber = requestedPage;

        // Always replace recipes for per-page display
        state.recipes = recipes;

        // Determine hasMore based on returned records count
        // If we get fewer records than pageSize, no more pages
        state.hasMore = recipes.length === state.pageSize;
      })
      .addCase(fetchRecipesByNameThunk.rejected, (state, action) => {
        state.isloading = false;
        state.error = action.payload;
      })

      .addCase(fetchCuisinesThunk.pending, (state) => {
        state.isloading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      .addCase(fetchCuisinesThunk.fulfilled, (state, action) => {
        state.isloading = false;
        state.apiCuisines = action.payload;
      })
      .addCase(fetchCuisinesThunk.rejected, (state, action) => {
        state.isloading = false;
        state.error = action.payload;
        state.updateFailedMessage = action.payload;
        state.updateFailedMessage = "No recipe found ";
      })

      .addCase(fetchTagsThunk.pending, (state) => {
        state.isloading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      .addCase(fetchTagsThunk.fulfilled, (state, action) => {
        state.isloading = false;
        state.apiTags = action.payload;
      })
      .addCase(fetchTagsThunk.rejected, (state, action) => {
        state.isloading = false;
        state.error = action.payload;
        state.updateFailedMessage = action.payload;
        state.updateFailedMessage = "No recipe found ";
      })

      .addCase(fetchFavoriteRecipesThunk.pending, (state) => {
        state.isloading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      .addCase(fetchFavoriteRecipesThunk.fulfilled, (state, action) => {
        state.isloading = false;
        state.recipes = action.payload;
        // Reset to page 1 for favorites
        state.pageNumber = 1;
        // Favorites typically don't have pagination
        state.hasMore = false;
      })
      .addCase(fetchFavoriteRecipesThunk.rejected, (state, action) => {
        state.isloading = false;
        state.error = action.payload;
        state.updateFailedMessage = action.payload;
      })

      .addCase(fetchAutocompleteRecipesThunk.pending, (state) => {
        state.isloading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      .addCase(fetchAutocompleteRecipesThunk.fulfilled, (state, action) => {
        state.isloading = false;
        state.autoCompleteNames = action.payload;
      })
      .addCase(fetchAutocompleteRecipesThunk.rejected, (state) => {
        state.isloading = false;
      });
  },
});
export const { clearUpdateMessage, resetRecipes, setPageNumber } =
  recipesListSlice.actions;
export const recipesListReducer = recipesListSlice.reducer;
